import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import { privateV2Api } from '@/lib/api-clients/rest-client';
import { useAccountStore } from '@/store/use-account-store';
import { getMarketStore } from '@/store/use-markets-store';
import { NewOrder } from '@/store/use-orders-store';
import {
  OrderStatus,
  OrderStatusV2,
  OrderType,
  OrderTypeV2,
  PositionSide,
  TimeInForceV2,
} from '@/types';
import { handleOrderForStore } from '@/utils/handle-store-data';
import { parseDecimalToBigInt } from '@/utils/value-format';

type QueryKey = [string, string];

interface OrderV2Response {
  account: string;
  account_group: number;
  created_at: number;
  execution_price: string;
  execution_time: number;
  expiration_time: number;
  leverage: string;
  nonce: number;
  order_id: string;
  order_type: OrderTypeV2;
  position_id: string;
  position_order_type: OrderTypeV2;
  price: string;
  quantity: string;
  reduce_only: boolean;
  side: PositionSide;
  signature: string;
  symbol: string;
  time_in_force: TimeInForceV2;
  updated_at: number;
  user_id: string;
}

export interface OpenOrderResponse extends OrderV2Response {
  status: OrderStatusV2.PENDING;
}

export const getOpenOrdersQueryKey = (apiKey: string | undefined) =>
  ['/positions/opened-orders', apiKey] as QueryKey;

export default function useOpenOrders() {
  const { apiKey, hasAuth, accGroup } = useAccountStore(
    useShallow((state) => {
      const authParams = state.getAuthParams();
      return {
        hasAuth: state.hasAuth,
        accGroup: authParams?.accountGroup,
        apiKey: authParams?.apiKey,
      };
    }),
  );
  return useQuery({
    enabled: !!apiKey && hasAuth && accGroup === 2,
    queryKey: getOpenOrdersQueryKey(apiKey),
    queryFn: (_) =>
      privateV2Api
        .get<{ orders: OpenOrderResponse[] }>('/v2/positions/opened-orders')
        .then((res) => {
          res.orders.forEach((data) => {
            const { marketSpec } = getMarketStore(data.symbol).getState();
            const leverage = parseDecimalToBigInt(data.leverage, 2n) || 100n;
            const initMarginRatio = 10n ** 6n / leverage;

            const limitPrice = parseDecimalToBigInt(
              data.price,
              marketSpec.priceDecimals,
            );
            const size = parseDecimalToBigInt(
              data.quantity,
              marketSpec.sizeDecimals,
            );

            let orderType: OrderType;

            switch (data.order_type) {
              case OrderTypeV2.MARKET:
                orderType = OrderType.MARKET;
                break;
              case OrderTypeV2.LIMIT:
                orderType = OrderType.LIMIT;
                break;
              case OrderTypeV2.LIQUIDATION:
                orderType = OrderType.LIQUIDATION;
                break;
              case OrderTypeV2.TAKE_PROFIT:
              case OrderTypeV2.MARKET_TAKE_PROFIT:
                orderType = OrderType.TAKE_PROFIT;
                break;
              case OrderTypeV2.STOP_LOSS:
              case OrderTypeV2.MARKET_STOP_LOSS:
                orderType = OrderType.STOP_LOSS;
                break;
            }

            const order: NewOrder = {
              id: data.order_id,
              symbol: data.symbol,
              initMarginRatio,
              isBuy: data.side === PositionSide.LONG,
              limitPrice,
              size,
              reduceOnly: data.reduce_only,
              status: OrderStatus.NEW,
              postTime: data.updated_at,
              orderType,
            };

            handleOrderForStore(order, { skipFill: true });
          });
          return res.orders;
        }),
  });
}
